import { fabric } from "fabric";
import { ImageLoadTask, LoadPriority, PlaceholderMapping } from "./types";
import { ViewportHelper } from "./ViewportHelper";

export class TaskQueue {
  private queue: ImageLoadTask[] = [];
  private viewportHelper: ViewportHelper;

  constructor(viewportHelper: ViewportHelper) {
    this.viewportHelper = viewportHelper;
  }

  public isEmpty(): boolean {
    return this.queue.length === 0;
  }

  public get length(): number {
    return this.queue.length;
  }

  public shift(): ImageLoadTask | undefined {
    return this.queue.shift();
  }

  public push(task: ImageLoadTask) {
    this.queue.push(task);
  }

  public clear() {
    this.queue = [];
  }

  public getAll(): ImageLoadTask[] {
    return this.queue;
  }

  /**
   * 添加任务到队列
   * 【优化】支持相同URL合并，避免重复下载
   */
  public add(placeholder: fabric.Object, originalObj: any, index: number = 0) {
    const src = originalObj.src;
    if (!src) return;

    // 检查是否在视口内
    const inViewport = this.viewportHelper.isInViewport(placeholder);

    // 查找是否已有相同URL的任务
    const existingTask = this.queue.find((t) => t.id === src);
    if (existingTask) {
      // 合并到已有任务
      existingTask.mappings.push({ placeholder, originalObj, index });

      // 如果当前映射在视口内，则更新任务的视口状态
      if (inViewport) {
        existingTask.inViewport = true;
        // 如果任务当前优先级低于 IN_VIEWPORT，则提升优先级
        if (existingTask.priority < LoadPriority.IN_VIEWPORT) {
          existingTask.priority = LoadPriority.IN_VIEWPORT;
        }
      }
    } else {
      // 创建新任务
      const task: ImageLoadTask = {
        id: src,
        mappings: [{ placeholder, originalObj, index }],
        priority: inViewport ? LoadPriority.IN_VIEWPORT : LoadPriority.OUT_OF_VIEWPORT,
        retries: 0,
        inViewport: inViewport
      };
      this.queue.push(task);
    }
  }

  /**
   * 批量添加任务
   * 【优化】自动按URL分组，相同URL只创建一个任务
   */
  public addBatch(tasks: { placeholder: fabric.Object; originalObj: any; index?: number }[]) {
    // 按URL分组
    const urlGroups = new Map<string, PlaceholderMapping[]>();

    tasks.forEach((t) => {
      const src = t.originalObj?.src;
      if (!src) return;

      if (!urlGroups.has(src)) {
        urlGroups.set(src, []);
      }
      urlGroups.get(src)!.push({
        placeholder: t.placeholder,
        originalObj: t.originalObj,
        index: t.index ?? 0
      });
    });

    // 为每个URL创建一个任务
    urlGroups.forEach((mappings, src) => {
      // 检查该组是否有任何映射在视口内
      const inViewport = mappings.some((m) => this.viewportHelper.isInViewport(m.placeholder));

      const task: ImageLoadTask = {
        id: src,
        mappings,
        priority: inViewport ? LoadPriority.IN_VIEWPORT : LoadPriority.OUT_OF_VIEWPORT,
        retries: 0,
        inViewport
      };
      this.queue.push(task);
    });
  }

  /**
   * 根据当前视口更新任务优先级
   */
  public updatePriorities() {
    const viewportInfo = this.viewportHelper.getViewportInfo();
    if (!viewportInfo) return;

    const { checkInViewport } = viewportInfo;

    this.queue.forEach((task) => {
      // 检测该URL的所有映射是否在视口内（任一在即可）
      const inViewport = task.mappings.some((m) => checkInViewport(m.placeholder));
      task.inViewport = inViewport;

      // 根据当前优先级和视口状态重新计算
      // 注意：Gallery优先级由 promoteGalleryImages 设置，这里只处理非Gallery任务
      if (task.priority < LoadPriority.GALLERY_OUT_VIEWPORT) {
        // 非Gallery任务，根据视口状态设置为 Level 0 或 Level 1
        task.priority = inViewport ? LoadPriority.IN_VIEWPORT : LoadPriority.OUT_OF_VIEWPORT;
      }
    });

    this.sort();
  }

  /**
   * 更新所有任务的视口状态（不修改优先级）
   */
  public updateViewportStatus(): void {
    const viewportInfo = this.viewportHelper.getViewportInfo();
    if (!viewportInfo) return;

    const { checkInViewport } = viewportInfo;

    this.queue.forEach((task) => {
      task.inViewport = task.mappings.some((m) => checkInViewport(m.placeholder));
    });
  }

  /**
   * 提升指定图片的优先级为 Gallery 级别
   */
  public promoteGalleryImages(urls: string[], inViewportMap?: Map<string, boolean>) {
    const urlSet = new Set(urls);
    let changed = false;

    this.queue.forEach((task) => {
      if (urlSet.has(task.id)) {
        // 确定视口状态：优先使用传入的map，否则使用缓存的状态
        const inViewport = inViewportMap?.get(task.id) ?? task.inViewport;
        task.inViewport = inViewport;

        // 计算新的优先级
        const newPriority = inViewport
          ? LoadPriority.GALLERY_IN_VIEWPORT // Level 3: Gallery + 在视口内
          : LoadPriority.GALLERY_OUT_VIEWPORT; // Level 2: Gallery + 不在视口内

        if (task.priority !== newPriority) {
          task.priority = newPriority;
          changed = true;
        }
      }
    });

    if (changed) {
      this.sort();
    }
  }

  /**
   * 对队列进行排序
   * 1. 优先级降序
   * 2. 同优先级下 index 降序
   */
  public sort() {
    this.queue.sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority; // 优先级高的在前
      }
      // 同优先级下，取该URL所有映射中的最大 index 进行比较
      const maxIndexA = Math.max(...a.mappings.map((m) => m.index));
      const maxIndexB = Math.max(...b.mappings.map((m) => m.index));
      return maxIndexB - maxIndexA; // 索引大的在前（倒序）
    });
  }

  /**
   * 获取当前队列中所有任务的统计信息
   */
  public getStats(): { total: number; byPriority: Record<string, number>; taskCount: number } {
    const stats = { total: 0, byPriority: {} as Record<string, number>, taskCount: this.queue.length };
    this.queue.forEach((task) => {
      const priorityName = LoadPriority[task.priority];
      const mappingCount = task.mappings.length;
      stats.byPriority[priorityName] = (stats.byPriority[priorityName] || 0) + mappingCount;
      stats.total += mappingCount;
    });
    return stats;
  }
}
